<template lang="pug">
  .app-calendar
    app-calendar-header(
      :current-month="currentMonth"
      :first-day="firstDay"
      :locale="locale"
      @change="emitChangeMonth"
    )
      template(slot="header-left")
        slot(name="header-left")
      template(slot="header-right")
        slot(name="header-right")

    .app-calendar-body
      .weeks
        strong.week(
          v-for="(day, index) in 7"
          :key="index"
        ) {{ index | localeWeekDay(firstDay, locale) }}

      .dates(ref="dates")
        .dates-background
          .week-row(
            v-for="(week, index) in currentDates"
            :key="`${week}-${index}`"
          )
            .day-cell(
              v-for="day in week"
              :class="dayClass(day)"
            )
              p.day-number {{ day.monthDay }}

        .dates-events
          .events-week(
            v-for="(week, weekIndex) in currentDates"
            :key="weekIndex"
          )
            .events-day(
              v-for="(day, dayIndex) in week"
              :class="dayClass(day)"
              :key="`${weekIndex}-${dayIndex}`"
              @click.stop="dayClick(day.date, $event)"
            )
              .is-flex(:style="{ 'justify-content': 'space-between' }")
                p
                  slot(
                    name="day-header"
                    :events="day.events"
                    :day="day"
                  )
                p.day-number {{ day.monthDay }}
              .event-box
                app-calendar-event-card(
                  v-for="(event, eventIndex) in day.events"
                  v-show="event.cellIndex <= eventLimit"
                  :event="event"
                  :date="day.date"
                  :first-day="firstDay"
                  :key="`${weekIndex}-${dayIndex}-${eventIndex}`"
                  @click="eventClick"
                )
                  template(slot-scope="props")
                    slot(
                      name="event-card"
                      :event="props.event"
                    )
                p.more-link(
                  v-if="day.events.length > eventLimit"
                  @click.stop="selectThisDay(day, $event)"
                ) + {{ day.events[day.events.length -1].cellIndex - eventLimit }} más

        .more-events(
          v-show="showMore"
          :style="moreEventsStyle"
        )
          .more-header.is-flex
            span.title.is-6.has-text-centered.is-marginless {{ moreTitle(selectDay.date) }}
            span.close(@click.stop="showMore = false")
              b-icon(
                size="is-small",
                icon="close"
              )
          .more-body
            ul.body-list
              li.body-item(
                v-for="(event, eventIndex) in selectDay.events"
                v-show="event.isShow"
                :key="eventIndex"
                @click="eventClick(event, $event)"
              ) {{event.title}}
                slot(
                  name="event-card"
                  :event="event"
                )
          slot(name="body-card")
</template>

<script>
// @TODO component forked from vue-fullcalendar pending to refactor

import moment from 'moment';

function getMonthViewStartDate(date, firstDay) {
  const internalFirstDay = parseInt(firstDay, 10);

  const start = moment(date);
  const startOfMonth = moment(start.startOf('month'));

  start.subtract(startOfMonth.day(), 'days');

  if (startOfMonth.day() < internalFirstDay) {
    start.subtract(7, 'days');
  }

  start.add(internalFirstDay, 'days');

  return start;
}

function getMonthViewEndDate() {
  return getMonthViewStartDate().add(6, 'weeks');
}

export default {
  name: 'AppCalendar',
  components: {
    AppCalendarHeader: () => import(/* webpackChunkName: "app-calendar-header" */ './AppCalendarHeader'),
    AppCalendarEventCard: () => import(/* webpackChunkName: "app-calendar-event-card" */ './AppCalendarEventCard'),
  },
  filters: {
    localeWeekDay(weekday, firstDay, locale) {
      const localMoment = moment().locale(locale);
      return localMoment.localeData().weekdaysShort()[(weekday + firstDay) % 7];
    },
  },
  props: {
    events: {
      type: Array,
      default: () => ([]),
    },
    locale: {
      type: String,
      default: 'es-MX',
    },
    firstDay: {
      type: Number,
      validator: value => value >= 0 && value <= 6,
      default: 0,
    },
    eventLimit: {
      type: Number,
      default: 3,
    },
  },
  data() {
    return {
      currentMonth: moment().startOf('month'),
      showMore: false,
      morePos: {
        top: 0,
        left: 0,
      },
      selectDay: {},
    };
  },
  computed: {
    dayClass() {
      return day => ({ today: day.isToday, 'not-cur-month': !day.isCurMonth });
    },
    currentDates() {
      const monthViewStartDate = getMonthViewStartDate(this.currentMonth, this.firstDay);

      const calendar = [];

      // eslint-disable-next-line no-plusplus
      for (let perWeek = 0; perWeek < 6; perWeek++) {
        const week = [];

        // eslint-disable-next-line no-plusplus
        for (let perDay = 0; perDay < 7; perDay++) {
          week.push({
            monthDay: monthViewStartDate.date(),
            isToday: monthViewStartDate.isSame(moment(), 'day'),
            isCurMonth: monthViewStartDate.isSame(this.currentMonth, 'month'),
            weekDay: perDay,
            date: moment(monthViewStartDate),
            events: this.slotEvents(monthViewStartDate),
          });

          monthViewStartDate.add(1, 'day');
        }

        calendar.push(week);
      }

      return calendar;
    },
    slotEvents() {
      return (date) => {
        const events = this.events.filter((event) => {
          const start = moment(event.start);
          const end = moment(event.end ? event.end : start);

          return date.isBetween(start.startOf('day'), end.endOf('day'), null, '[]');
        }).sort((a, b) => {
          if (!a.cellIndex) return 1;
          if (!b.cellIndex) return -1;

          return a.cellIndex - b.cellIndex;
        }).map((dayEvent, index) => {
          const event = dayEvent;

          event.cellIndex = event.cellIndex || (index + 1);
          event.isShow = true;

          if (event.cellIndex === index + 1 || index > 2) return event;

          return {
            title: 'holder',
            cellIndex: index + 1,
            start: date.format(),
            end: date.format(),
            isShow: false,
          };
        });

        return events;
      };
    },
    moreEventsStyle() {
      return { left: `${this.morePos.left}px`, top: `${this.morePos.top}px` };
    },
    moreTitle() {
      return (date) => {
        if (!date) return '';
        return moment(date).format('YYYY/MM/DD');
      };
    },
  },
  mounted() {
    this.emitChangeMonth(this.currentMonth);
  },
  methods: {
    emitChangeMonth(firstDayOfMonth) {
      this.currentMonth = firstDayOfMonth;

      const start = getMonthViewStartDate(firstDayOfMonth, this.firstDay);
      const end = getMonthViewEndDate(firstDayOfMonth, this.firstDay);

      this.$emit('change:month', start, end, firstDayOfMonth);
    },
    selectThisDay(day, jsEvent) {
      this.selectDay = day;
      this.showMore = true;
      this.morePos = this.computePos(jsEvent.target);
      this.morePos.top -= 100;

      const events = day.events.filter(item => item.isShow === true);

      this.$emit('click:more', day.date, events, jsEvent);
    },
    computePos(target) {
      const eventRect = target.getBoundingClientRect();

      const pageRect = this.$refs.dates.getBoundingClientRect();

      return {
        left: eventRect.left - pageRect.left,
        top: eventRect.top + eventRect.height - pageRect.top,
      };
    },
    dayClick(day, jsEvent) {
      this.$emit('click:day', day, jsEvent);
    },
    eventClick(event, jsEvent) {
      if (!event.isShow) return;

      jsEvent.stopPropagation();

      const pos = this.computePos(jsEvent.target);

      this.$emit('click:event', event, jsEvent, pos);
    },
  },
};
</script>

<style lang="stylus" scoped>
.comp-full-calendar
  padding 20px
  background #fff
  max-width 960px
  margin 0 auto

ul
p
  margin 0
  padding 0

.app-calendar-body
  padding-top 20px

.weeks
  display flex
  border-top 1px solid #e0e0e0
  border-bottom 1px solid #e0e0e0
  border-left 1px solid #e0e0e0

.week
  flex 1
  text-align center
  border-right 1px solid #e0e0e0

.dates
  position relative

.week-row
  border-left 1px solid #e0e0e0;
  display flex

.day-cell
  flex 1
  min-height 112px
  padding 4px
  border-right 1px solid #e0e0e0
  border-bottom 1px solid #e0e0e0

.day-number
  text-align right

.today
  background #d2ebf5

.not-cur-month .day-number
  color rgba(0, 0, 0, .24)

.dates-events
  position absolute
  top 0
  left 0
  z-index 1
  width 100%

.events-week
  display flex

.events-day
  cursor pointer
  flex 1
  min-height 112px
  overflow hidden
  text-overflow ellipsis

  .day-number
    text-align right
    padding 4px 5px 4px 4px
    opacity 0

.events-day.today .day-number
  opacity 1
  color black
  font-weight 600

.more-events
  position absolute
  width 150px
  z-index 2
  border 1px solid #eee
  box-shadow 0 2px 6px rgba(0,0,0,.15)

.more-header
  background-color #eee
  padding 5px
  display flex
  align-items center
  font-size 14px

  .title
    flex 1

.close
  margin-right 2px
  cursor pointer
  font-size 16px

.more-body
  height 146px
  overflow hidden

.body-list
  height 144px
  padding 5px
  overflow auto
  background-color #fff

.body-item
  cursor pointer
  font-size 12px
  background-color #C7E6FD
  margin-bottom 2px
  color rgba(0, 0, 0, .87)
  padding 0 0 0 4px
  height 18px
  line-height 18px
  white-space nowrap
  overflow hidden
  text-overflow ellipsis

.event-box
  padding 0 .25rem
</style>
